/* =========================================================================
   Place des Arts — runtime i18n du JS.

   Expose `window.PDA.t(cle, params)`, `PDA.locale`, `PDA.estFrancais()`, `PDA.nombre()`.
   Le dictionnaire est descendu par `snippets/pda-i18n.liquid` (généré depuis
   `theme/tools/i18n/dictionnaire.mjs`, drapeau `js: true`) ; les décisions de
   nommage sont dans `specs/i18n-dictionnaire.md`.

   ── ORDRE DE CHARGEMENT — L'INVARIANT EXACT, MESURÉ LE 20/08 ────────────────
   Chargé en <head> avec `defer`. Ce que ça garantit, et ce que ça NE garantit pas :

   ✅ Il précède TOUS les `type="module"`, y compris ceux chargés par les sections
      (`main-collection`, `search-results`, `pda-wishlist-grid`, `pda-artists-catalog`,
      `pda-filters`) — les scripts différés et les modules s'exécutent dans l'ordre du
      document. ⛔ SOUS UNE CONDITION QUI N'EST PAS ACQUISE : qu'aucun module CONSOMMANT
      `PDA` ne porte `async`. La phrase qui vivait ici — « aucun module du thème ne porte
      `async` (22 balises vérifiées) » — était FAUSSE dans les deux moitiés :
      `snippets/jumbo-text.liquid` charge `jumbo-text.js` en `type="module" async`, et le
      thème en compte des dizaines, pas 22. ⛔ AUCUN CHIFFRE PRÉCIS ICI : deux comptages
      faits le même jour ont donné 83 et 84, et un nombre qu'aucun script ne régénère
      pourrit. `jumbo-text.js` n'utilise pas `PDA`, donc
      rien ne casse — mais un futur consommateur marqué `async` s'exécuterait AVANT ce
      runtime et peindrait des clés nues, en silence. La règle complète est dans
      `layout/theme.liquid`, au point de chargement ; ne pas la recopier, s'y référer.
      Les `<script>` des réponses Section Rendering sont INERTES (`DOMParser`).
   ⛔ Il ne précède AUCUN `<script>` INLINE CLASSIQUE : ceux-là s'exécutent PENDANT
      l'analyse, donc AVANT tout script différé. Le thème en compte plusieurs, dont
      `blocks/pda-filters.liquid` et `sections/pda-artists-catalog.liquid` — les deux
      fichiers les plus denses en chaînes FR. **Un `PDA.t()` posé dans un inline
      classique lèverait un TypeError dur.** Si une de ces chaînes doit être traduite,
      il faut la rendre en Liquid (`| t`), pas l'appeler depuis le JS.
   ⚠️ Il n'est PAS « le premier script du document » : 28 différés le précèdent (aucun
      n'utilise `PDA`). Cette description a été fausse une fois, elle est corrigée ici.

   ⛔ LECTURE PARESSEUSE du bloc JSON, au premier appel de `t()` — supprime toute
   dépendance d'ordre entre ce script et le bloc qu'il lit.

   ⛔ ÉCHOUER FORT, JAMAIS EN SILENCE. Clé absente ⇒ on peint la CLÉ (comme Shopify
   peint « Translation missing ») et on crie. Paramètre manquant ⇒ le placeholder
   reste littéral et on crie.
   ========================================================================= */
(function () {
  'use strict';

  var PDA = (window.PDA = window.PDA || {});

  var dico = null; // { locale, t: { cle: string | {one,other} } }
  var criees = Object.create(null); // messages déjà signalés — une seule fois chacun

  function crier(message) {
    if (criees[message]) return;
    criees[message] = true;
    // console.error et pas throw : un libellé manquant ne doit pas tuer la grille.
    console.error('[pda-i18n] ' + message);
  }

  /* ⛔ LE FILTRE LIQUID `| t` HTML-ÉCHAPPE SA SORTIE. Mesuré le 20/08 sur le HTML
   * servi : la locale stocke « Pièce d'exception », le bloc descend
   * « Pièce d&#39;exception ». C'est correct dans du HTML, FAUX dans un bloc de
   * données lu en texte — la valeur est ensuite peinte en `textContent`, donc
   * l'entité s'afficherait telle quelle.
   * Toute valeur portant ' " & < > est concernée ; l'anglais et l'italien en
   * introduiront à chaque traduction (« l'artista », « Artist's »).
   * ⛔ NE PAS corriger par une liste de `replace` : un contrôle par liste d'interdits
   * ne voit que ce qu'on y a mis (règle de méthode #4) — le prochain traducteur
   * écrira l'entité qui n'y est pas.
   * Le <textarea> est un élément RCDATA : son innerHTML décode les entités et
   * n'exécute RIEN (ni script, ni gestionnaire d'événement). `| t` n'échappe qu'une
   * fois, donc un seul décodage suffit. */
  var deco = null;
  function decoder(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    if (!deco) deco = document.createElement('textarea');
    deco.innerHTML = s;
    return deco.value;
  }

  function decoderTout(o) {
    var out = {};
    for (var cle in o) {
      if (!Object.prototype.hasOwnProperty.call(o, cle)) continue;
      var v = o[cle];
      if (v && typeof v === 'object') {
        var formes = {};
        for (var f in v) if (Object.prototype.hasOwnProperty.call(v, f)) formes[f] = decoder(v[f]);
        out[cle] = formes;
      } else {
        out[cle] = decoder(v);
      }
    }
    return out;
  }

  function charger() {
    if (dico) return dico;
    var el = document.querySelector('[data-pda-i18n]');
    if (!el) {
      /* ⛔ ON NE MÉMORISE PAS L'ÉCHEC. Un seul appel trop tôt (script inline classique,
       * cf. l'invariant d'ordre en tête) figerait un dictionnaire VIDE pour toute la vie
       * de la page : chaque `t()` ultérieur peindrait sa clé nue, et `crier()` ne
       * parlerait qu'une fois. Des clés partout à l'écran pour une seule ligne de
       * console, c'est l'inverse d'« échouer fort ». On rend donc un objet JETABLE. */
      crier('bloc [data-pda-i18n] absent — appel trop précoce (script inline ?) ou snippet non rendu par le layout.');
      return { locale: 'fr', t: {} };
    }
    try {
      var j = JSON.parse(el.textContent);
      dico = { locale: j.locale || 'fr', t: decoderTout(j.t || {}) };
    } catch (e) {
      crier('bloc [data-pda-i18n] illisible : ' + e.message);
      return { locale: 'fr', t: {} }; // idem : jetable, pas mémorisé
    }
    return dico;
  }

  /* Catégorie plurielle CLDR de la locale active.
   * ⛔ PAS `n !== 1`. Le code d'origine fabriquait le pluriel ainsi, ce qui est faux
   * DÈS LE FRANÇAIS : `Intl.PluralRules('fr').select(0)` rend « one », donc
   * « 0 résultat » et non « 0 résultats ». En allemand comme en italien, la forme ne
   * s'obtient pas non plus en collant un « s ». */
  var regles = null;
  var reglesKo = false;
  function categorie(n) {
    var loc = charger().locale;
    if (reglesKo) return n === 1 ? 'one' : 'other';
    try {
      if (!regles || regles.__loc !== loc) {
        regles = new Intl.PluralRules(loc);
        regles.__loc = loc;
      }
      return regles.select(n);
    } catch (e) {
      /* Repli mémorisé ET signalé : sans ça on reconstruirait l'objet à chaque appel,
       * et surtout ce repli est EXACTEMENT la règle que ce fichier condamne — en `fr`
       * il ferait retomber 0 sur « other ». Il ne doit jamais passer inaperçu. */
      reglesKo = true;
      crier('Intl.PluralRules indisponible pour « ' + loc +' » — repli n!==1, INEXACT en français (0 → « other »).');
      return n === 1 ? 'one' : 'other';
    }
  }

  /**
   * Substitue les `{{ nom }}`. Un paramètre attendu et absent CRIE.
   *
   * ⛔ UN PARAMÈTRE NUMÉRIQUE EST MIS AU FORMAT DE LA LOCALE, ici et pas chez
   * l'appelant. Sinon le point d'appel doit formater lui-même — et il ne le peut
   * pas sans casser autre chose : formater avant l'appel produit une CHAÎNE, ce qui
   * fait perdre au runtime le nombre dont il a besoin pour choisir la forme
   * plurielle. Le contrat est donc : **on passe les nombres BRUTS**, le runtime
   * pluralise ET formate. (Vécu : un premier jet avait contourné par un second
   * `.replace('{{ count }}', …)` sur une chaîne déjà substituée — inopérant.)
   */
  function interpoler(gabarit, params, cle) {
    return String(gabarit).replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, function (brut, nom) {
      if (params && Object.prototype.hasOwnProperty.call(params, nom) && params[nom] != null) {
        var v = params[nom];
        return typeof v === 'number' ? PDA.nombre(v) : String(v);
      }
      crier('paramètre « ' + nom + ' » manquant pour la clé « ' + cle + ' »');
      return brut; // on laisse le gabarit VISIBLE plutôt que de peindre du vide
    });
  }

  /**
   * @param {string} cle    — clé canonique, ex. 'pda.catalogue.resultat'
   * @param {object} [params] — valeurs des placeholders ; `count` pilote aussi le pluriel
   * @returns {string}
   */
  PDA.t = function (cle, params) {
    var d = charger();
    /* hasOwnProperty : `t` vient de `JSON.parse`, il hérite donc d'Object.prototype —
     * `d.t['constructor']` rendrait une FONCTION, truthy, et on peindrait « function… ». */
    var v = Object.prototype.hasOwnProperty.call(d.t, cle) ? d.t[cle] : undefined;

    if (v === undefined || v === null) {
      crier(v === null ? 'clé « ' + cle + ' » à null dans la locale' : 'clé inconnue : ' + cle);
      return cle; // visible à l'écran, comme le « Translation missing » de Shopify
    }

    if (typeof v === 'object') {
      var n = params && params.count;
      if (typeof n !== 'number') {
        crier('clé pluralisée « ' + cle + ' » appelée sans `count` numérique');
        n = 0;
      }
      var cat = categorie(n);
      // Les langues publiées n'ont que one/other ; `other` est le repli universel.
      var forme = Object.prototype.hasOwnProperty.call(v, cat) ? v[cat] : v.other;
      if (forme === undefined || forme === null) {
        crier('clé « ' + cle + ' » sans forme « ' + cat + ' » ni « other »');
        return cle;
      }
      return interpoler(forme, params, cle);
    }

    return interpoler(v, params, cle);
  };

  /* Locale active (`request.locale.iso_code`), pour les décisions de format.
   * ⛔ Le setter est un no-op DÉLIBÉRÉ, pas un oubli : sans lui, `PDA.locale = 'en'`
   * depuis un `type="module"` lève un TypeError — les modules ES sont toujours en
   * strict mode, donc ça ne dégraderait pas, ça TUERAIT le module appelant (donc la
   * grille). `enumerable` pour rester visible dans un dump de debug. */
  Object.defineProperty(PDA, 'locale', {
    enumerable: true,
    configurable: true,
    get: function () { return charger().locale; },
    set: function () { crier('PDA.locale est en LECTURE SEULE — la locale vient de request.locale.iso_code.'); },
  });

  /** Vrai si la locale active est le français — garde des mécaniques grammaticales FR. */
  PDA.estFrancais = function () { return String(charger().locale || '').toLowerCase().indexOf('fr') === 0; };

  /**
   * Formatage de nombre suivant la locale ACTIVE.
   * ⛔ Remplace les `toLocaleString('fr-FR')` figés du moteur : un visiteur anglophone
   * lisait « 1 132 » avec une espace insécable là où il attend « 1,132 ».
   */
  PDA.nombre = function (n) {
    try { return Number(n).toLocaleString(charger().locale); } catch (e) { return String(n); }
  };

  /**
   * Formatage NU : séparateur décimal de la locale, JAMAIS de séparateur de milliers.
   * ⛔ RÉSERVÉ AUX ZONES MACHINE — les champs de fourchette (prix, dimensions, année),
   * dont la valeur est RELUE par un parseur qui lit « , » et « . » comme des décimales.
   * Cote Sam 29/08 : « pas de séparateur de milliers » — la virgule décimale française,
   * elle, RESTE (sinon le champ afficherait « 15.7 » à côté d'un chip « 15,7 in »).
   * Un groupe de milliers ici casserait la relecture : « 1,950 » (en) se relit 1.95.
   */
  /**
   * Lecture d'un nombre SAISI PAR UN HUMAIN, dans la locale active.
   * ⛔ Le placeholder de ce même champ affiche « 10.000+ » à un Allemand et
   * « 10,000+ » à un Anglais : un parseur français y lisait 10. Mesuré le 29/08 sur
   * le live — « 5.000 » tapé en de rendait `?prix_max=5`, chip « ≤ 5 € », ZÉRO œuvre,
   * et « 1.960 » en Année repeignait « 2 ». L'écran invitait à tomber dedans.
   * Les séparateurs se DÉRIVENT de la locale (formatToParts), ils ne se supposent pas.
   * ⚠️ AMBIGUÏTÉ IRRÉDUCTIBLE, assumée : « 10.000 » vaut 10000 en de/it/es et 10 en en.
   * Aucun parseur ne peut trancher dans l'absolu ⇒ on lit ce que la locale ÉCRIT,
   * c'est-à-dire exactement ce que le placeholder propose au visiteur.
   */
  PDA.parseNombre = function (txt) {
    const brut = String(txt == null ? '' : txt).trim();
    if (!brut) return NaN;
    let groupe = '', dec = ',';
    try {
      for (const p of new Intl.NumberFormat(charger().locale).formatToParts(12345.6)) {
        if (p.type === 'group') groupe = p.value;
        else if (p.type === 'decimal') dec = p.value;
      }
    } catch (e) { /* repli : conventions FR, celles du thème par défaut */ }
    // Espaces de toute largeur et apostrophes : séparateurs de groupe dans TOUTES les
    // conventions servies — retirés avant de consulter la locale.
    let net = brut.replace(/[\s'\u2019]/g, '');
    if (groupe && !/^\s*$/.test(groupe)) net = net.split(groupe).join('');
    if (dec !== '.') net = net.split(dec).join('.');
    return parseFloat(net);
  };

  PDA.nombreNu = function (n) {
    try { return Number(n).toLocaleString(charger().locale, { useGrouping: false }); }
    catch (e) { return String(n); }
  };

  /**
   * Nationalité localisée depuis son nom FRANÇAIS (celui que sert l'index CDN).
   * SOURCE UNIQUE — le mécanisme vivait dans pda-artists-catalog.js (LOT 4, 25/08) ;
   * déplacé ici le 25/08 quand la grille « Artistes similaires »
   * (pda-similar-artists.js) a été mesurée rendant « France »/« Espagne » en dur dans
   * les 5 locales : elle ne passait pas par la table. Deux copies du même bloc, c'est
   * deux endroits où oublier un alias — le précédent des quatre `* 2.54` de la métrique.
   * Mécanisme (mesuré le 25/08 sur les 79 valeurs de l'index) : Intl.DisplayNames 'fr'
   * → table inversée nomFR→ISO (676 codes scannés UNE fois, au premier appel) →
   * dnLoc.of(iso) — 71 ISO directes + 5 alias de graphie + 3 irréductibles en dur
   * (« Alaska » n'est pas un pays ISO ; « Corée » reste sans résolution N/S — on
   * traduit le MOT, on ne tranche pas une donnée). Registre ratifié : l'anglais passe
   * par en-GB, jamais en-US.
   * ⛔ La locale se lit sur `document.documentElement.lang` (posé par Shopify au SSR),
   * PAS sur `charger().locale` : un appel avant que le bloc [data-pda-i18n] soit
   * analysable recevrait le dictionnaire JETABLE (locale 'fr') et ce cache-ci — qui,
   * lui, est définitif — figerait la localisation ÉTEINTE pour toute la page.
   * Rend null si : locale FR, Intl absent, ou lookup raté — l'appelant garde alors le
   * FR tel quel : visible au differ, jamais silencieusement faux.
   */
  var natResolveur; // undefined = pas construit · null = inapplicable (fr / sans Intl)
  PDA.nationaliteAffichee = function (nomFr) {
    if (natResolveur === undefined) {
      natResolveur = (function () {
        var lang = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
        if (lang === 'fr' || typeof Intl === 'undefined' || !Intl.DisplayNames) return null;
        var normN = function (s) { return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim(); };
        var ALIAS = { 'bosnie': 'BA', 'cap vert': 'CV', 'republique tcheque': 'CZ', 'vietnam': 'VN', 'macedoine': 'MK' };
        var MANUELS = {
          'alaska': { en: 'Alaska', de: 'Alaska', it: 'Alaska', es: 'Alaska' },
          'coree': { en: 'Korea', de: 'Korea', it: 'Corea', es: 'Corea' },
          'congo': { en: 'Congo', de: 'Kongo', it: 'Congo', es: 'Congo' },
        };
        try {
          var dnFr = new Intl.DisplayNames(['fr'], { type: 'region' });
          var dnLoc = new Intl.DisplayNames([lang === 'en' ? 'en-GB' : lang], { type: 'region' });
          var versIso = {};
          for (var a = 65; a <= 90; a++) {
            for (var b = 65; b <= 90; b++) {
              var code = String.fromCharCode(a) + String.fromCharCode(b);
              try { var n = dnFr.of(code); if (n && n !== code) versIso[normN(n)] = code; } catch (e) { /* code non régional */ }
            }
          }
          return function (nom) {
            var k = normN(nom);
            var manuel = MANUELS[k];
            if (manuel) return manuel[lang] || null;
            var iso = versIso[k] || ALIAS[k];
            if (!iso) return null;
            try { return dnLoc.of(iso) || null; } catch (e) { return null; }
          };
        } catch (e) { return null; }
      })();
    }
    return natResolveur ? natResolveur(nomFr) : null;
  };

  /* ── Réécriture de requête vers la RACINE FRANÇAISE (option H, 25/08) ─────────
   * « Landschaft » → « Paysage » : la recherche interroge la racine FR pour toutes
   * les locales (pda-catalog.js §runTextSearch) — un visiteur étranger qui tapait
   * dans SA langue ne trouvait rien, faute de matière traduite à chercher
   * (Context/ANALYSE-recherche-et-pill-2026-08-24.md §1.7). Avant l'envoi, les
   * EXPRESSIONS de la requête connues du vocabulaire sont réécrites vers leur
   * valeur française canonique. L'AFFICHAGE garde toujours ce que l'utilisateur
   * a TAPÉ — la réécriture ne vit que dans l'URL du fetch.
   * ⛔ BORNÉ AU VOCABULAIRE DU SITE : la source est la table [data-pda-moteur-labels]
   * (snippet GÉNÉRÉ du lot 4 — te/su/st/mv/bd/sg/o + ty/tg via titres de
   * collections), INVERSÉE ici. Jamais un lexique général : un mot hors
   * vocabulaire reste tel quel, et doit rendre 0 (contre-épreuve de l'analyse).
   * Pièges encodés (chacun testé dans tests/i18n-reecriture.test.mjs) :
   *  · expression LA PLUS LONGUE d'abord (« original poster » avant « poster ») —
   *    réécrire token par token casserait les multi-mots ;
   *  · frontières de MOTS : « landschaft » ne matche pas DANS « landschaften » ;
   *  · collision (même libellé localisé → DEUX valeurs FR différentes) = entrée
   *    ABANDONNÉE, jamais un choix silencieux ; même valeur FR (su/o « Landscape »
   *    → « Paysage ») = gardée ;
   *  · cognats (libellé ≡ valeur FR normalisés) = ignorés (no-op de toute façon) ;
   *  · locale FR, table absente ou JSON illisible = IDENTITÉ — la recherche
   *    dégrade vers le comportement d'avant, jamais cassée ;
   *  · garde < 3 caractères : elle droppe UNE entrée réelle, de « Öl » (huile) —
   *    NFD retire le tréma, « ol » fait 2 caractères (mesuré 25/08, seule perte
   *    sur les 4 locales, figée en test). Perte de rappel assumée : élargir la
   *    garde rouvrirait les mots trop courts/ambigus.
   * ⛔ Locale sur `documentElement.lang`, jamais via le chargeur du dictionnaire
   * (même invariant que les nationalités ci-dessus, mêmes raisons — un appel
   * précoce recevrait le dictionnaire JETABLE). Le cache est DÉFINITIF. */
  var reqResolveur; // undefined = pas construit · null = inapplicable (fr / sans table)
  PDA.requeteRacineFr = function (requete) {
    var q = String(requete == null ? '' : requete);
    if (reqResolveur === undefined) {
      reqResolveur = (function () {
        var lang = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
        if (lang === 'fr') return null;
        var el = document.querySelector('[data-pda-moteur-labels]');
        if (!el) return null;
        var table;
        try { table = JSON.parse(el.textContent); } catch (e) { return null; }
        var normR = function (s) { return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim(); };
        var vers = {};     // libellé localisé normalisé → valeur FR canonique
        var conflits = {};
        for (var fam in table) {
          if (!Object.prototype.hasOwnProperty.call(table, fam)) continue;
          var paires = table[fam];
          for (var fr in paires) {
            if (!Object.prototype.hasOwnProperty.call(paires, fr)) continue;
            var loc = paires[fr];
            if (typeof loc !== 'string') continue;
            var k = normR(loc);
            if (!k || k.length < 3) continue;            // garde anti-générique minimale
            if (k === normR(fr)) continue;               // cognat
            if (conflits[k]) continue;
            if (Object.prototype.hasOwnProperty.call(vers, k) && normR(vers[k]) !== normR(fr)) {
              delete vers[k]; conflits[k] = true;        // deux FR ≠ pour un même mot : on n'invente pas
              continue;
            }
            vers[k] = fr;
          }
        }
        var cles = Object.keys(vers);
        if (!cles.length) return null;
        var parNbTokens = {};  // nb de tokens → { expression normalisée: valeur FR }
        var maxTokens = 1;
        for (var i = 0; i < cles.length; i++) {
          var n = cles[i].split(' ').length;
          if (n > maxTokens) maxTokens = n;
          (parNbTokens[n] = parNbTokens[n] || {})[cles[i]] = vers[cles[i]];
        }
        return function (texte) {
          var mots = String(texte).trim().split(/\s+/).filter(Boolean);
          if (!mots.length) return texte;
          var out = [];
          var pos = 0;
          while (pos < mots.length) {
            var trouve = null;
            var saut = 0;
            for (var t = Math.min(maxTokens, mots.length - pos); t >= 1 && !trouve; t--) {
              var groupe = parNbTokens[t];
              if (!groupe) continue;
              var expr = normR(mots.slice(pos, pos + t).join(' '));
              if (Object.prototype.hasOwnProperty.call(groupe, expr)) { trouve = groupe[expr]; saut = t; }
            }
            if (trouve) { out.push(trouve); pos += saut; } else { out.push(mots[pos]); pos += 1; }
          }
          return out.join(' ');
        };
      })();
    }
    return reqResolveur ? reqResolveur(q) : q;
  };
})();
